/**
 * overlay.js — Canvas overlay drawn on top of the PDF.js page canvas.
 *
 * Layer draw order (bottom → top):
 *   1. Layout boxes    (blue outlines)     — toggleable
 *   2. Table outlines  (green outlines)    — toggleable
 *   3. Text highlights (translucent yellow fill) — toggleable
 *   4. Search results  (yellow/orange fill)
 *   5. User annotations (red outlines + labels)
 *   6. Draw-in-progress box (while mouse is dragged)
 *
 * All stored rects are in PDF-point space at scale=1.
 * At repaint time they are multiplied by the current render scale.
 *
 * Layer visibility is controlled via setLayerVisible(layer, bool).
 */

/** @typedef {{ x: number, y: number, w: number, h: number }} Rect */

// ── State ─────────────────────────────────────────────────────────────────

let _canvas    = null
let _ctx       = null
let _container = null
let _scale     = 1.0
let _drawMode  = false

// Layer data (PDF-point space, scale=1)
let _layoutBoxes    = []
let _tableBoxes     = []
let _textHighlights = []
let _annotations    = []
let _searchState    = { rects: [], currentIdx: -1 }
let _calloutRects   = []   // PDF-point rects for table_callout highlights

// Layer visibility (matches Qt InspectorPanel toggles)
const _layerVisible = {
  layout: true,
  tables: true,
  text:   false,
}

// Batching — suppresses repaint() until endBatch()
let _batching   = false

// rAF throttle for mousemove during drawing
let _rafPending = false

// Draw-in-progress state (physical canvas-pixel coords)
let _drawing     = false
let _drawStart   = null
let _drawCurrent = null

// Active bbox — set after user finishes drawing (PDF-point space)
let _activeBbox = null   // Rect | null

// DOM button that floats over the active bbox
let _extractBtn = null

// Callbacks
/** @type {((rect: Rect) => void)|null} */
let _onAnnotationDrawn  = null
/** @type {(() => void)|null} */
let _onExtractTable     = null
/** @type {(() => void)|null} */
let _onBboxCleared      = null

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Attach the overlay canvas to a container element.
 * @param {HTMLElement} container  The #page-wrapper element
 */
export function attach(container) {
  _container = container

  const existing = container.querySelector('canvas.overlay-canvas')
  if (existing) existing.remove()

  _canvas = document.createElement('canvas')
  _canvas.className = 'overlay-canvas'
  Object.assign(_canvas.style, {
    position:      'absolute',
    top:           '0',
    left:          '0',
    pointerEvents: 'auto',
    zIndex:        '10',
  })
  container.style.position = 'relative'
  container.appendChild(_canvas)
  _ctx = _canvas.getContext('2d')

  // Floating "Extract Table" button (hidden until a bbox is drawn)
  const existingBtn = container.querySelector('.bbox-extract-btn')
  if (existingBtn) existingBtn.remove()
  _extractBtn = document.createElement('button')
  _extractBtn.className   = 'bbox-extract-btn'
  _extractBtn.textContent = 'Pull Table'
  Object.assign(_extractBtn.style, {
    position:      'absolute',
    display:       'none',
    zIndex:        '20',
    padding:       '5px 14px',
    background:    'rgba(30, 120, 255, 0.92)',
    color:         '#fff',
    border:        'none',
    borderRadius:  '5px',
    cursor:        'pointer',
    fontSize:      '13px',
    fontWeight:    '600',
    boxShadow:     '0 2px 8px rgba(0,0,0,0.4)',
    whiteSpace:    'nowrap',
    pointerEvents: 'auto',
    userSelect:    'none',
  })
  _extractBtn.addEventListener('mousedown', e => e.stopPropagation())
  _extractBtn.addEventListener('click', () => { if (_onExtractTable) _onExtractTable() })
  container.appendChild(_extractBtn)

  _canvas.addEventListener('mousedown', _onMouseDown)
  _canvas.addEventListener('mousemove', _onMouseMove)
  _canvas.addEventListener('mouseup',   _onMouseUp)

  // Dismiss bbox on click anywhere outside it
  document.addEventListener('mousedown', _onDocMouseDown, true)
}

/**
 * Size the overlay canvas to match the PDF canvas and repaint.
 */
export function onPageRender(pdfCanvas, scale) {
  if (!_canvas) return
  _scale               = scale
  _canvas.style.visibility = ''
  _canvas.width        = pdfCanvas.width
  _canvas.height       = pdfCanvas.height
  _canvas.style.width  = pdfCanvas.style.width  || pdfCanvas.width  + 'px'
  _canvas.style.height = pdfCanvas.style.height || pdfCanvas.height + 'px'
  _positionExtractBtn()
  repaint()
}

export function setScale(scale) {
  _scale = scale
  _positionExtractBtn()
  repaint()
}

/** Set the active bbox (PDF-point space) — shows translucent overlay + button. */
export function setActiveBbox(rect) {
  _activeBbox = rect
  _positionExtractBtn()
  repaint()
}

/** Dismiss the active bbox and hide the Extract Table button. */
export function clearActiveBbox() {
  _activeBbox = null
  if (_extractBtn) _extractBtn.style.display = 'none'
  repaint()
}

export function hasActiveBbox() {
  return !!_activeBbox
}

/** Register callback for when the Extract Table button is clicked. */
export function setExtractTableCallback(cb) { _onExtractTable = cb }

/** Register callback for when the active bbox is dismissed. */
export function setOnBboxCleared(cb) { _onBboxCleared = cb }

/** Suppress repaints until endBatch() — avoids redundant redraws. */
export function beginBatch() { _batching = true }
export function endBatch()   { _batching = false; repaint() }

export function setLayoutBoxes(rects) { _layoutBoxes = rects || []; repaint() }
export function setTableBoxes(rects)  { _tableBoxes = rects || [];  repaint() }
export function setTextHighlights(rects) { _textHighlights = rects || []; repaint() }

export function setSearchHighlights(rects, currentIdx) {
  _searchState = { rects: rects || [], currentIdx }
  repaint()
}

/**
 * Persistent yellow highlights for table callouts (PDF-point rects).
 * Unlike search highlights these survive page re-renders and are set by the
 * callout-filtered viewer tab.
 */
export function setCalloutHighlights(rects) {
  _calloutRects = rects || []
  repaint()
}

export function setAnnotations(annotations) {
  _annotations = annotations || []
  repaint()
}

export function setDrawMode(enabled) {
  _drawMode = enabled
  if (_canvas) {
    if (enabled) {
      // Custom crosshair: dark strokes with white outline — visible on any background
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'>
        <line x1='12' y1='1'  x2='12' y2='10' stroke='white' stroke-width='3'/>
        <line x1='12' y1='14' x2='12' y2='23' stroke='white' stroke-width='3'/>
        <line x1='1'  y1='12' x2='10' y2='12' stroke='white' stroke-width='3'/>
        <line x1='14' y1='12' x2='23' y2='12' stroke='white' stroke-width='3'/>
        <line x1='12' y1='1'  x2='12' y2='10' stroke='black' stroke-width='1.5'/>
        <line x1='12' y1='14' x2='12' y2='23' stroke='black' stroke-width='1.5'/>
        <line x1='1'  y1='12' x2='10' y2='12' stroke='black' stroke-width='1.5'/>
        <line x1='14' y1='12' x2='23' y2='12' stroke='black' stroke-width='1.5'/>
      </svg>`
      const encoded = 'data:image/svg+xml,' + encodeURIComponent(svg)
      _canvas.style.cursor = `url("${encoded}") 12 12, crosshair`
    } else {
      _canvas.style.cursor = 'default'
    }
  }
  if (!enabled) { _drawing = false; _drawStart = null; _drawCurrent = null; repaint() }
}

/** Register callback fired when annotation draw completes (PDF-point rect). */
export function onAnnotationDrawn(cb) { _onAnnotationDrawn = cb }

export function hide() { if (_canvas) _canvas.style.visibility = 'hidden' }
export function show() { if (_canvas) _canvas.style.visibility = '' }

/**
 * Toggle layer visibility (matches Qt InspectorPanel checkboxes).
 * @param {'layout'|'tables'|'text'} layer
 * @param {boolean} visible
 */
export function setLayerVisible(layer, visible) {
  _layerVisible[layer] = visible
  repaint()
}

/** Get current layer visibility state. */
export function getLayerVisible() {
  return { ..._layerVisible }
}

/** Force full repaint. */
export function repaint() {
  if (_batching || !_ctx || !_canvas) return
  _ctx.clearRect(0, 0, _canvas.width, _canvas.height)

  if (_layerVisible.layout)
    _drawStrokeLayer(_layoutBoxes, 'rgba(0,120,255,0.6)', 1.5)
  if (_layerVisible.tables)
    _drawStrokeLayer(_tableBoxes, 'rgba(0,180,80,0.7)', 1.5)
  if (_layerVisible.text)
    _drawFillLayer(_textHighlights, 'rgba(255,220,0,0.3)')

  // Search highlights (always visible)
  for (let i = 0; i < _searchState.rects.length; i++) {
    const px  = _toPx(_searchState.rects[i])
    const act = i === _searchState.currentIdx
    _ctx.fillStyle = act ? 'rgba(255,140,0,0.55)' : 'rgba(255,220,0,0.35)'
    _ctx.fillRect(px.x, px.y, px.w, px.h)
    if (act) {
      _ctx.strokeStyle = 'rgba(255,80,0,0.9)'
      _ctx.lineWidth   = 2
      _ctx.strokeRect(px.x, px.y, px.w, px.h)
    }
  }

  // Callout highlights — translucent yellow fill (padded upstream)
  if (_calloutRects.length) {
    _ctx.save()
    for (const r of _calloutRects) {
      const px = _toPx(r)
      _ctx.fillStyle   = 'rgba(255,225,0,0.30)'
      _ctx.fillRect(px.x, px.y, px.w, px.h)
      _ctx.strokeStyle = 'rgba(240,180,0,0.85)'
      _ctx.lineWidth   = 2
      _ctx.strokeRect(px.x, px.y, px.w, px.h)
    }
    _ctx.restore()
  }

  // Annotations
  _ctx.save()
  for (const ann of _annotations) {
    const px = _toPx(ann.rect)
    _ctx.strokeStyle = 'rgba(30,140,255,0.9)'
    _ctx.lineWidth   = 2
    _ctx.strokeRect(px.x, px.y, px.w, px.h)
    if (ann.label) {
      _ctx.font      = `${Math.max(11, 12 * _scale)}px sans-serif`
      _ctx.fillStyle = 'rgba(30,140,255,0.95)'
      _ctx.fillText(ann.label, px.x + 3, px.y - 3)
    }
  }
  _ctx.restore()

  // Active bbox — translucent blue fill + solid border
  if (_activeBbox) {
    const px = _toPx(_activeBbox)
    _ctx.save()
    _ctx.fillStyle   = 'rgba(30, 120, 255, 0.18)'
    _ctx.fillRect(px.x, px.y, px.w, px.h)
    _ctx.strokeStyle = 'rgba(30, 120, 255, 0.9)'
    _ctx.lineWidth   = 2
    _ctx.strokeRect(px.x, px.y, px.w, px.h)
    _ctx.restore()
  }

  // Draw-in-progress — translucent fill + dashed border
  if (_drawing && _drawStart && _drawCurrent) {
    const x = Math.min(_drawStart.x, _drawCurrent.x)
    const y = Math.min(_drawStart.y, _drawCurrent.y)
    const w = Math.abs(_drawCurrent.x - _drawStart.x)
    const h = Math.abs(_drawCurrent.y - _drawStart.y)
    _ctx.save()
    _ctx.fillStyle   = 'rgba(30, 120, 255, 0.10)'
    _ctx.fillRect(x, y, w, h)
    _ctx.strokeStyle = 'rgba(30, 120, 255, 0.9)'
    _ctx.lineWidth   = 1.5
    _ctx.setLineDash([5, 3])
    _ctx.strokeRect(x, y, w, h)
    _ctx.setLineDash([])
    _ctx.restore()
  }
}

// ── Drawing helpers ──────────────────────────────────────────────────────

/** CSS scale: CSS pixels per PDF point. */
function _cssScale() {
  if (!_canvas) return _scale
  const dpr = _canvas.width / (_canvas.offsetWidth || _canvas.width)
  return _scale / dpr
}

/** Position the floating Extract Table button just below the active bbox. */
function _positionExtractBtn() {
  if (!_extractBtn || !_activeBbox) return
  const cs  = _cssScale()
  const bx  = _activeBbox.x * cs
  const by  = _activeBbox.y * cs
  const bw  = _activeBbox.w * cs
  const bh  = _activeBbox.h * cs
  const btnW = 130
  const containerW = _container ? _container.offsetWidth : 9999
  let left = bx + bw / 2 - btnW / 2
  left = Math.max(4, Math.min(containerW - btnW - 4, left))
  _extractBtn.style.left    = `${left}px`
  _extractBtn.style.top     = `${by + bh + 6}px`
  _extractBtn.style.display = 'block'
}

function _toPx(r) {
  return { x: r.x * _scale, y: r.y * _scale, w: r.w * _scale, h: r.h * _scale }
}

function _drawStrokeLayer(rects, color, lw) {
  if (!rects.length) return
  _ctx.save()
  _ctx.strokeStyle = color
  _ctx.lineWidth   = lw
  for (const r of rects) { const px = _toPx(r); _ctx.strokeRect(px.x, px.y, px.w, px.h) }
  _ctx.restore()
}

function _drawFillLayer(rects, color) {
  if (!rects.length) return
  _ctx.save()
  _ctx.fillStyle = color
  for (const r of rects) { const px = _toPx(r); _ctx.fillRect(px.x, px.y, px.w, px.h) }
  _ctx.restore()
}

// ── Pointer events ────────────────────────────────────────────────────────

/** Convert CSS-pixel mouse offset → physical canvas pixels. */
function _canvasPx(offsetX, offsetY) {
  const dpr = _canvas ? (_canvas.width / (_canvas.offsetWidth || _canvas.width)) : 1
  return { x: offsetX * dpr, y: offsetY * dpr }
}

function _onMouseDown(e) {
  if (e.button !== 0) return

  if (_drawMode) {
    // Starting a new draw — clear any existing active bbox first
    if (_activeBbox) { clearActiveBbox(); if (_onBboxCleared) _onBboxCleared() }
    e.preventDefault()
    _drawing     = true
    _drawStart   = _canvasPx(e.offsetX, e.offsetY)
    _drawCurrent = _canvasPx(e.offsetX, e.offsetY)
    return
  }

  // Not in draw mode: dismiss if clicking outside the active bbox
  if (_activeBbox) {
    const cs = _cssScale()
    const inside = e.offsetX >= _activeBbox.x * cs &&
                   e.offsetX <= (_activeBbox.x + _activeBbox.w) * cs &&
                   e.offsetY >= _activeBbox.y * cs &&
                   e.offsetY <= (_activeBbox.y + _activeBbox.h) * cs
    if (!inside) { clearActiveBbox(); if (_onBboxCleared) _onBboxCleared() }
  }
}

function _onMouseMove(e) {
  if (!_drawing) return
  _drawCurrent = _canvasPx(e.offsetX, e.offsetY)
  if (!_rafPending) {
    _rafPending = true
    requestAnimationFrame(() => { _rafPending = false; repaint() })
  }
}

function _onMouseUp(e) {
  if (!_drawing || e.button !== 0) return
  _drawing = false
  const x0 = Math.min(_drawStart.x, _drawCurrent.x)
  const y0 = Math.min(_drawStart.y, _drawCurrent.y)
  const w  = Math.abs(_drawCurrent.x - _drawStart.x)
  const h  = Math.abs(_drawCurrent.y - _drawStart.y)
  const shiftHeld = e.shiftKey
  _drawStart = null; _drawCurrent = null

  if (w > 4 && h > 4) {
    const rect = { x: x0 / _scale, y: y0 / _scale, w: w / _scale, h: h / _scale }
    _activeBbox = rect
    _positionExtractBtn()
    repaint()
    if (_onAnnotationDrawn) _onAnnotationDrawn(rect, { shiftSnap: shiftHeld })
  } else {
    repaint()
  }
}

/** Dismiss bbox when clicking outside both the canvas and the extract button. */
function _onDocMouseDown(e) {
  if (!_activeBbox) return
  if (_extractBtn && (e.target === _extractBtn || _extractBtn.contains(e.target))) return
  if (e.target === _canvas) return
  clearActiveBbox()
  if (_onBboxCleared) _onBboxCleared()
}
